// ─────────────────────────────────────────────────────────────────────────────
// Reports.jsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useState, useEffect } from "react";
import CustomPieChart, { computeFitStatus } from './CustomPieChart';
// CheatingEvidence — rendered inline (within Reports' own layout) when
// the user clicks "View evidence →" in PlagiarismCard AND no parent
// passed onOpenEvidence. This keeps the standalone /reports route
// embedded-style: clicking the link swaps the body to the dashboard
// without a full-page navigation; Back returns here.
import CheatingEvidence from '../../CheatingEvidence/CheatingEvidence';
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Heading, Text } from '@/Components/ui/typography';
import { Stack, Inline, Grid } from '@/Components/ui/layout';
import { Badge } from '@/Components/ui/badge';
import { Separator } from '@/Components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/Components/ui/table';
import { Container } from '@/Components/ui/container';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import {
  Download, ClipboardCheck, ChevronLeft, FileText, BarChart3,
  CheckCircle2, XCircle, Code2, FileCode, Building2, Zap,
  Brain, Mic, AlertTriangle, Sparkles, Cpu, TrendingUp, MessageSquare,
  ChevronDown, ChevronRight, Loader2, FlaskConical, Search, User, X,
  Calculator, ShieldAlert,
} from 'lucide-react';
import {
  Dialog, DialogContent,
  DialogTitle, DialogDescription,
} from "@/Components/ui/dialog";
import { EmployerLayout } from '../../JobPostPricing/EmployerLayout';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/config/api';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const pick  = (...vals) => vals.find(v => v !== undefined && v !== null && v !== "") ?? null;
const toNum = (v, fallback = null) => {
  if (v === undefined || v === null || v === "") return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
};
const toText = (...vals) => { const v = pick(...vals); return v == null ? "" : String(v); };
const toList = (value) => {
  if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
  if (typeof value === "string")
    return value.replace(/[\[\]']/g, "").split(",").map(v => v.trim()).filter(Boolean);
  return [];
};

const getScoreColor = (score) => {
  if (score >= 85) return 'bg-green-500';
  if (score >= 70) return 'bg-blue-500';
  if (score >= 50) return 'bg-yellow-500';
  if (score >= 30) return 'bg-orange-500';
  return 'bg-red-500';
};

// Hex version — safe for print windows where Tailwind classes don't resolve
const getScoreColorHex = (score) => {
  if (score >= 85) return '#22c55e';
  if (score >= 70) return '#3b82f6';
  if (score >= 50) return '#eab308';
  if (score >= 30) return '#f97316';
  return '#ef4444';
};

const getScoreLabel = (score) => {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Moderate';
  if (score >= 30) return 'Low';
  return 'Very Low';
};

const getFeedback = (type, score) => {
  const map = {
    pitch:      ['Inconsistent','Flat','Monotonous','Uneven','Slightly varied','Balanced','Smooth','Calm','Consistent','Excellent'],
    volume:     ['Too low','Low','Uneven','Soft','Adequate','Clear','Strong','Bold','High','Perfect'],
    speechRate: ['Very fast','Fast','Slightly fast','Moderate','Balanced','Clear','Steady','Very clear','Smooth','Perfect'],
    pauses:     ['Awkward','Too frequent','Long','Inconsistent','Distracting','Good','Well-placed','Natural','Great','Excellent'],
    grammar:    ['Poor','Many mistakes','Frequent errors','Needs improvement','Fair','Average','Good','Professional','Very good','Excellent'],
  };
  return map[type]?.[score - 1] || 'N/A';
};

// ─────────────────────────────────────────────────────────────────────────────
// Score-bar sub-component
// ─────────────────────────────────────────────────────────────────────────────

const ScoreBar = ({ label, value, outOf = 100 }) => {
  const pct = Math.min(100, (value / outOf) * 100);
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
      <Text size="sm" className="font-medium min-w-0">{label}</Text>
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className={cn("h-full rounded-full transition-all", getScoreColor(pct))} style={{ width: `${pct}%` }} />
        </div>
        <Text size="sm" className="font-semibold min-w-[3rem] text-right">
          {outOf === 100 ? `${Math.round(value)}%` : `${value}/${outOf}`}
        </Text>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Score descriptions
// ─────────────────────────────────────────────────────────────────────────────

const scoreDescriptions = {
  Introversion_vs_Extroversion: [
    "Very reserved, avoids social interaction, prefers solo tasks.",
    "Introverted, participates when needed but prefers quiet environments.",
    "Moderately outgoing, balances group and individual work.",
    "Confident communicator, active in discussions, energizes team.",
    "Highly extroverted, thrives in social settings, natural leader and speaker.",
  ],
  Emotional_Stability: [
    "Easily stressed, anxious in high-pressure situations.",
    "Sensitive to criticism, may show emotional fluctuation.",
    "Generally calm but can be reactive under prolonged stress.",
    "Emotionally composed, handles stress and criticism well.",
    "Exceptionally calm and resilient, maintains stability in any situation.",
  ],
  Openness_to_Experience: [
    "Prefers routine and tradition, resistant to new ideas.",
    "Cautious toward change, prefers familiar systems.",
    "Open to ideas with some reservation; evaluates change before adoption.",
    "Curious and imaginative, embraces new perspectives and tools.",
    "Highly innovative and experimental, eager to explore the unknown.",
  ],
  Agreeableness: [
    "Competitive or critical, may cause interpersonal friction.",
    "Occasionally cooperative but may struggle with empathy.",
    "Reasonably cooperative and understanding in most cases.",
    "Empathetic, promotes harmony and helps resolve conflicts.",
    "Highly compassionate and generous, promotes strong team bonding.",
  ],
  Conscientiousness: [
    "Unorganized, often misses deadlines or details.",
    "Needs guidance and structure to stay on task.",
    "Generally responsible, but may need supervision.",
    "Reliable, organized, self-motivated to complete tasks.",
    "Extremely detail-oriented, proactive, sets high personal standards.",
  ],
  Problem_Solving: [
    "Struggles to identify issues and find solutions independently.",
    "Occasionally identifies problems, but needs support solving them.",
    "Able to solve familiar problems with some guidance.",
    "Effectively analyzes problems and generates practical solutions.",
    "Highly analytical, proactive problem-solver with strategic thinking.",
  ],
  Communication_Skills: [
    "Has difficulty expressing ideas clearly and listening effectively.",
    "Can communicate basic points but may lack clarity or engagement.",
    "Communicates clearly in routine situations, sometimes persuasive.",
    "Effectively conveys complex ideas and listens actively.",
    "Exceptional communicator, inspires and connects with diverse audiences.",
  ],
  Handling_Pressure: [
    "Struggles to maintain performance in stressful situations.",
    "Shows signs of stress under pressure, may need frequent support.",
    "Can perform under pressure with occasional lapses.",
    "Remains composed and focused in challenging situations.",
    "Thrives under pressure, demonstrates resilience and high performance.",
  ],
  Ownership_and_Responsibility: [
    "Avoids accountability, often shifts blame or neglects duties.",
    "Reluctant to take ownership, requires close supervision.",
    "Takes responsibility for assigned work with some follow-through.",
    "Demonstrates strong ownership, meets commitments reliably.",
    "Fully accountable, proactive in taking initiative and responsibility.",
  ],
  Values_and_Ethics: [
    "Disregards ethical standards, makes decisions for personal gain.",
    "Sometimes compromises values under pressure or influence.",
    "Generally ethical but may need reminders or guidance.",
    "Strong sense of integrity, adheres to company and personal ethics.",
    "Highly principled, consistently demonstrates integrity and fairness.",
  ],
  // Communication test traits
  Clarity: [
    "Very unclear, hard to follow or understand.",
    "Frequent ambiguities; ideas lack structure.",
    "Generally clear with occasional unclear passages.",
    "Clear and well-structured expression.",
    "Exceptionally clear; ideas are expressed precisely and concisely.",
  ],
  Grammar_Proficiency: [
    "Frequent grammatical errors affecting comprehension.",
    "Many mistakes; language is unprofessional.",
    "Occasional errors; generally acceptable language.",
    "Good grammar with minor lapses.",
    "Flawless grammar and polished professional language.",
  ],
  Tone_and_Professionalism: [
    "Unprofessional or inappropriate tone throughout.",
    "Inconsistent tone; occasionally informal.",
    "Generally professional with minor tone issues.",
    "Professional, respectful, and context-appropriate.",
    "Exemplary professionalism; inspires confidence.",
  ],
  Listening: [
    "Does not demonstrate understanding of questions.",
    "Frequently misinterprets or ignores the question intent.",
    "Answers on-topic most of the time.",
    "Clearly understands and directly addresses questions.",
    "Outstanding comprehension; picks up on nuance and subtext.",
  ],
  Persuasiveness: [
    "Responses fail to make any persuasive impact.",
    "Weak arguments; lacks conviction.",
    "Moderately persuasive with some supporting points.",
    "Compelling and well-reasoned arguments.",
    "Highly persuasive; responses are memorable and impactful.",
  ],
  // Coding traits
  efficiency:       ["Code is inefficient and overly complex.","Basic optimization, often slow.","Moderately efficient.","Mostly efficient, minimal complexity.","Highly efficient and elegant code."],
  performance:      ["Frequent bottlenecks.","Below-average performance.","Meets general standards.","Performs well under load.","Exceptionally high-performing."],
  robustness:       ["Breaks easily, no error handling.","Limited reliability.","Reasonably robust.","Reliable; anticipates edge cases.","Extremely robust."],
  readability_score:["Poorly structured, hard to maintain.","Inconsistent formatting.","Average readability.","Clean and well-organized.","Highly readable and well-documented."],
  correctness:      ["Fails requirements, many bugs.","Partially correct.","Mostly correct, minor issues.","Functionally correct, few bugs.","Fully correct, passes all edge cases."],
  code_modularity:  ["Monolithic, no structure.","Minimal separation of concerns.","Some modular structure.","Well-structured with reusable components.","Highly modular, clean separation of concerns."],
};

const getDescription = (score, trait) => {
  const descs = scoreDescriptions[trait];
  if (!descs) return "No description available.";
  if (score <= 2) return descs[0];
  if (score <= 4) return descs[1];
  if (score <= 6) return descs[2];
  if (score <= 8) return descs[3];
  return descs[4];
};

// ─────────────────────────────────────────────────────────────────────────────
// Reports component
// ─────────────────────────────────────────────────────────────────────────────

const Reports = ({
  embedded = false,
  candidate: candidateProp,
  jobDetails: jobDetailsProp,
  // When provided, the "View evidence" link inside PlagiarismCard calls
  // this callback with the coding_test_id instead of triggering a
  // full-page navigation. The PARENT then renders <CheatingEvidence
  // embedded /> in the SAME shell. When NOT provided, Reports handles
  // it itself via local state so the standalone /reports route also
  // stays inside its own EmployerLayout instead of navigating away.
  onOpenEvidence: onOpenEvidenceProp = null,
} = {}) => {
  // Local fallback state — used when no parent callback was wired.
  // The standalone /reports route uses this path: clicking "View
  // evidence" sets the id, the body of Reports below early-returns
  // <CheatingEvidence embedded /> inside the same EmployerLayout, and
  // CheatingEvidence's Back button clears the state to return here.
  const [localEvidenceTestId, setLocalEvidenceTestId] = useState(null);
  // Test type for the embedded dashboard: null → coding (all tabs); a
  // non-coding type (screening/pnb/communication/apt) hides the code-only tabs.
  const [localEvidenceType, setLocalEvidenceType] = useState(null);
  // Effective callback — parent's wins, otherwise our local setter.
  // Accepts (id, type): PlagiarismCard calls it with just the id (coding →
  // type undefined), the proctoring buttons pass a type. Either way the same
  // inline dashboard opens in this shell — no full-page navigation.
  const onOpenEvidence = onOpenEvidenceProp || ((id, type = null) => {
    setLocalEvidenceTestId(id);
    setLocalEvidenceType(type);
  });
  const location = useLocation();
  const navigate  = useNavigate();
  // Source of truth for data:
  //   • Default (top-level /reports route): read from location.state — set by
  //     the company-side `navigate('/reports', { state: { candidate, jobDetails } })`.
  //   • Embedded mode (inside admin panel): read from props directly, since we
  //     can't nest routers to inject synthetic location.state.
  const candidate  = candidateProp  ?? location.state?.candidate;
  const jobDetails = jobDetailsProp ?? location.state?.jobDetails;
  const reportRef = useRef();
  // When `embedded` is true (e.g. rendered inside the admin panel), we skip
  // the EmployerLayout wrapper so the host page's chrome (sidebar/header) stays
  // visible instead of being shadowed by the employer-side sidebar.
  const LayoutWrapper = embedded ? React.Fragment : EmployerLayout;
  const [codingModalOpen,    setCodingModalOpen]    = useState(false);
  const [screeningModalOpen, setScreeningModalOpen] = useState(false);
  const [aptitudeModalOpen,  setAptitudeModalOpen]  = useState(false);
  const [communicationModalOpen, setCommunicationModalOpen] = useState(false);
  const [pnbModalOpen, setPnbModalOpen] = useState(false);

  const data = candidate || {};
  const base = data.details || {};
  const tests = data.tests || {};

  const screening     = tests.screening     || {};
  const coding        = tests.coding        || {};
  const personality   = tests.personality   || {};
  const behavioral    = tests.behavioral    || {};
  const communication = tests.communication || {};   // ← new
  const aptitude      = tests.aptitude      || {};

  // ── normalised data object ────────────────────────────────────────────────
  const d = {
    ...base,

    name:            toText(base.name, base.full_name),
    Overall_Summary: toText(base.Overall_Summary, base.overall_summary),
    Strengths:       toText(base.Strengths,        base.strengths),
    Missing_Skills:  toText(base.Missing_Skills,   base.missing_skills),

    cur_ctc:      toText(base.cur_ctc),
    exp_ctc:      toText(base.exp_ctc),
    notice_period:toText(base.notice_period),

    Resume_Score: toNum(base.Resume_Score ?? base.resume_score, 0),
    final_score:  toNum(base.final_score, 0),

    screening_score:     toNum(base.screening_score     ?? base.screening_overall_score     ?? screening.overall_score,     null),
    personality_score:   toNum(base.personality_score   ?? base.personality_overall_score   ?? personality.score,           null),
    behavioral_score:    toNum(base.behavioral_score    ?? base.behavioral_overall_score    ?? behavioral.score,            null),
    coding_score:        toNum(base.coding_score        ?? base.coding_overall_score        ?? coding.overall_score,        null),
    communication_score: toNum(base.communication_score ?? base.communication_overall_score ?? communication.overall_score, null),
    aptitude_score:      toNum(base.aptitude_score      ?? base.aptitude_overall_score      ?? aptitude.overall_score,      null),

    screening_scheduled_time:     base.screening_scheduled_time     || null,
    personality_scheduled_time:   base.personality_scheduled_time   || null,
    behavioral_scheduled_time:    base.behavioral_scheduled_time     || null,
    coding_scheduled_time:        base.coding_scheduled_time        || null,
    communication_scheduled_time: base.communication_scheduled_time || null,

    // Technical
    domain_score:    toNum(screening.domain_score    ?? base.domain_score,    0),
    confidence_score:toNum(screening.confidence_score?? base.confidence_score,0),
    Pitch:           toNum(screening.pitch           ?? base.Pitch,           0),
    Volume:          toNum(screening.volume          ?? base.Volume,          0),
    rate:            toNum(screening.rate            ?? base.rate,            0),
    Pauses:          toNum(screening.pauses          ?? base.Pauses,          0),
    Grammar_Score:   toNum(screening.grammar_score   ?? base.Grammar_Score,   0),
    Screening_Strength:   toText(screening.screening_strength,   base.Screening_Strength),
    Screening_Weakness:   toText(screening.screening_weakness,   base.Screening_Weakness),
    Screening_Highlights: toText(screening.screening_highlights, base.Screening_Highlights),
    topics:        toText(screening.topics,        base.topics),
    topics_scores: toText(screening.topics_scores, base.topics_scores),

    // Personality
    Introversion_vs_Extroversion: toNum(personality.introversion_vs_extroversion ?? base.Introversion_vs_Extroversion, null),
    Emotional_Stability:          toNum(personality.emotional_stability          ?? base.Emotional_Stability,          null),
    Openness_to_Experience:       toNum(personality.openness_to_experience       ?? base.Openness_to_Experience,       null),
    Agreeableness:                toNum(personality.agreeableness                ?? base.Agreeableness,                null),
    Conscientiousness:            toNum(personality.conscientiousness            ?? base.Conscientiousness,            null),
    Personality_Strength: toText(personality.personality_strength, base.Personality_Strength),
    Personality_Weakness: toText(personality.personality_weakness, base.Personality_Weakness),

    // Behavioural
    Problem_Solving:              toNum(behavioral.problem_solving              ?? base.Problem_Solving,              null),
    Communication_Skills:         toNum(behavioral.communication_skills         ?? base.Communication_Skills,         null),
    Handling_Pressure:            toNum(behavioral.handling_pressure            ?? base.Handling_Pressure,            null),
    Ownership_and_Responsibility: toNum(behavioral.ownership_and_responsibility ?? base.Ownership_and_Responsibility, null),
    Values_and_Ethics:            toNum(behavioral.values_and_ethics            ?? base.Values_and_Ethics,            null),
    Behavioral_Strength: toText(behavioral.behavioral_strength, base.Behavioral_Strength),
    Behavioral_Weakness: toText(behavioral.behavioral_weakness, base.Behavioral_Weakness),

    // Coding
    plagiarism_percentage:   toNum(coding.plagiarism_percentage   ?? screening.plagiarism_percentage ?? base.plagiarism_percentage, null),
    correctness:             toNum(coding.correctness             ?? base.correctness,             0),
    efficiency:              toNum(coding.efficiency              ?? base.efficiency,              0),
    readability_score:       toNum(coding.readability_score       ?? base.readability_score,       0),
    performance:             toNum(coding.performance             ?? base.performance,             0),
    robustness:              toNum(coding.robustness              ?? base.robustness,              0),
    code_architecture:       toText(coding.code_architecture,        base.code_architecture),
    performance_optimization:toText(coding.performance_optimization, base.performance_optimization),
    algorithm_selection:     toText(coding.algorithm_selection,      base.algorithm_selection),
    code_modularity:         toNum(coding.code_modularity          ?? base.code_modularity,          0),
    coding_summary:          toText(coding.coding_summary           ?? coding.summary,   base.coding_summary),
    coding_strengths:        toText(coding.coding_strengths         ?? coding.strengths, base.coding_strengths),
    coding_weaknesses:       toText(coding.coding_weaknesses        ?? coding.weaknesses,base.coding_weaknesses),

    // Communication (new)
    Clarity:                toNum(communication.clarity                ?? base.Clarity,                null),
    Grammar_Proficiency:    toNum(communication.grammar_proficiency    ?? base.Grammar_Proficiency,    null),
    Tone_and_Professionalism:toNum(communication.tone_and_professionalism ?? base.Tone_and_Professionalism, null),
    Listening:              toNum(communication.listening              ?? base.Listening,              null),
    Persuasiveness:         toNum(communication.persuasiveness         ?? base.Persuasiveness,         null),
    Communication_Strength: toText(communication.communication_strength, base.Communication_Strength),
    Communication_Weakness: toText(communication.communication_weakness, base.Communication_Weakness),

    // Aptitude
    aptitude_accuracy:          toNum(aptitude.aptitude_accuracy       ?? base.aptitude_accuracy,       null),
    aptitude_voice_score:       toNum(aptitude.aptitude_voice_score    ?? base.aptitude_voice_score,    null),
    aptitude_attempted_count:   toNum(aptitude.aptitude_attempted_count?? base.aptitude_attempted_count, null),
    aptitude_correct_count:     toNum(aptitude.aptitude_correct_count  ?? base.aptitude_correct_count,  null),
    aptitude_total_questions:   toNum(aptitude.aptitude_total_questions?? base.aptitude_total_questions, null),
    aptitude_section_breakdown: aptitude.aptitude_section_breakdown    ?? base.aptitude_section_breakdown ?? null,
    Aptitude_Strengths:         toText(aptitude.aptitude_strengths, base.aptitude_strengths),
    Aptitude_Weaknesses:        toText(aptitude.aptitude_weaknesses, base.aptitude_weaknesses),
  };

  // ── availability flags ────────────────────────────────────────────────────
  const hasScreeningTest     = d.screening_score     != null && d.screening_score     > 0;
  const hasPersonalityTest   = d.personality_score   != null && d.personality_score   > 0;
  const hasBehavioralTest    = d.behavioral_score    != null && d.behavioral_score    > 0;
  const hasCodingTest        = d.coding_score        != null;   // show even when score is 0 (candidate submitted, no code)
  const hasCommunicationTest = d.communication_score != null && d.communication_score > 0;
  const hasAptitudeTest      = d.aptitude_score != null;   // show even at 0 (violation-forced submit)
  const hasCodingTestScheduled = d.coding_scheduled_time != null;
  const hasPlagiarismData      = d.plagiarism_percentage != null;
  const hasAnyTest = hasScreeningTest || hasPersonalityTest || hasBehavioralTest || hasCodingTest || hasCommunicationTest || hasAptitudeTest;

  // ── average score (only from completed tests) ─────────────────────────────
  const availableScores = [
    hasScreeningTest     ? d.screening_score     : null,
    hasPersonalityTest   ? d.personality_score   : null,
    hasBehavioralTest    ? d.behavioral_score     : null,
    hasCodingTest        ? d.coding_score        : null,
    hasCommunicationTest ? d.communication_score : null,
    hasAptitudeTest      ? d.aptitude_score      : null,
  ].filter(s => s != null);

  // ── aptitude section rows (from the JSONB breakdown) ─────────────────────
  const aptitudeSections = (() => {
    let sb = d.aptitude_section_breakdown;
    if (typeof sb === 'string') { try { sb = JSON.parse(sb); } catch { sb = null; } }
    if (!sb || typeof sb !== 'object') return [];
    return Object.entries(sb)
      .filter(([, v]) => v && typeof v === 'object')
      .map(([key, v]) => ({
        key,
        label: v.label || key,
        score: v.score != null ? Number(v.score) : null,
        attempted: v.attempted ?? null,
        correct: v.correct ?? null,
        total: v.total ?? null,
      }));
  })();

  const roundedAverage = availableScores.length
    ? Math.round(availableScores.reduce((a, b) => a + b, 0) / availableScores.length)
    : 0;

  // ── fit status (uses shared logic from CustomPieChart) ────────────────────
  const fitStatus = computeFitStatus(
    roundedAverage,
    availableScores,
    d.plagiarism_percentage,
  );

  const FIT_BADGE = {
    'Strong Fit':   'bg-green-100  text-green-800  border-green-300',
    'Moderate Fit': 'bg-blue-100   text-blue-800   border-blue-300',
    'Weak Fit':     'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Poor Fit':     'bg-red-100    text-red-800    border-red-300',
  };

  // ── topic table data ──────────────────────────────────────────────────────
  let formattedTopics = [];
  if (hasScreeningTest && d.topics) {
    const topics = toList(d.topics);
    const scores = toList(d.topics_scores);
    formattedTopics = topics.map((topic, idx) => ({ topic, score: scores[idx] ?? null }));
  }

  const generateTopicDescription = (topic, score) => {
    if (score >= 8) return `High proficiency in ${topic}`;
    if (score >= 6) return `Moderate understanding of ${topic}`;
    if (score >= 4) return `Basic familiarity with ${topic}`;
    return `Needs improvement in ${topic}`;
  };

  // ── trait tables ──────────────────────────────────────────────────────────
  const personalityTraits  = [
    { key: 'Introversion_vs_Extroversion', label: 'Extroversion'         },
    { key: 'Emotional_Stability',          label: 'Emotional Stability'   },
    { key: 'Openness_to_Experience',       label: 'Openness to Experience'},
    { key: 'Agreeableness',                label: 'Agreeableness'         },
    { key: 'Conscientiousness',            label: 'Conscientiousness'     },
  ];
  const behavioralTraits = [
    { key: 'Problem_Solving',              label: 'Problem Solving'           },
    { key: 'Communication_Skills',         label: 'Communication Skills'      },
    { key: 'Handling_Pressure',            label: 'Handling Pressure'         },
    { key: 'Ownership_and_Responsibility', label: 'Ownership & Responsibility'},
    { key: 'Values_and_Ethics',            label: 'Values and Ethics'         },
  ];
  const communicationTraits = [
    { key: 'Clarity',                 label: 'Clarity'               },
    { key: 'Grammar_Proficiency',     label: 'Grammar & Language'    },
    { key: 'Tone_and_Professionalism',label: 'Tone & Professionalism'},
    { key: 'Listening',               label: 'Listening / Comprehension'},
    { key: 'Persuasiveness',          label: 'Persuasiveness'        },
  ];

  const nonTechnicalScores = [...behavioralTraits, ...personalityTraits].filter(({ key }) => typeof d[key] === 'number');
  const hasNonTechnicalScores = nonTechnicalScores.length > 0;
  const hasCommunicationTraits = communicationTraits.some(({ key }) => typeof d[key] === 'number');

  // ── insights panels ───────────────────────────────────────────────────────
  const parseList = (str) =>
    str ? str.replace(/[\[\]']+/g, "").split(",").map(s => s.trim()).filter(Boolean) : [];

  const testInsights = [
    {
      title: 'Technical Test',
      icon: '/technical-support.png',
      strengths:  parseList(d.Screening_Strength),
      weaknesses: parseList(d.Screening_Weakness),
    },
    {
      title: 'Coding Test',
      icon: '/code.png',
      strengths:  parseList(d.coding_strengths),
      weaknesses: parseList(d.coding_weaknesses),
    },
    {
      title: 'Non-Technical Test',
      icon: '/mind-map.png',
      strengths:  [...parseList(d.Personality_Strength), ...parseList(d.Behavioral_Strength)],
      weaknesses: [...parseList(d.Personality_Weakness), ...parseList(d.Behavioral_Weakness)],
    },
    {
      title: 'Communication Test',
      icon: '/communication.png',
      strengths:  parseList(d.Communication_Strength),
      weaknesses: parseList(d.Communication_Weakness),
    },
    {
      title: 'Aptitude Test',
      icon: '/mind-map.png',
      strengths:  parseList(d.Aptitude_Strengths),
      weaknesses: parseList(d.Aptitude_Weaknesses),
    },
  ].filter(t => t.strengths.length > 0 || t.weaknesses.length > 0);

  // ── print handler ─────────────────────────────────────────────────────────
  const handleBrowserPrint = () => {
    if (!reportRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Pop-up blocked', { description: 'Please allow pop-ups to download the PDF report.', duration: 5000 });
      return;
    }
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.href).filter(Boolean);
    const inlineStyles = Array.from(document.querySelectorAll('style')).map(s => s.innerHTML).join('\n');
    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Candidate Report</title>
      ${stylesheets.map(h => `<link rel="stylesheet" href="${h}">`).join('')}
      <style>${inlineStyles}@media print{*{-webkit-print-color-adjust:exact!important}body{margin:0;padding:15px}}</style>
    </head><body>${reportRef.current.innerHTML}</body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
  };

  // ── no data guard ─────────────────────────────────────────────────────────
  if (!candidate || !hasAnyTest) {
    return (
      <LayoutWrapper>
        <div className="flex flex-col w-full gap-4">
          {!embedded && (
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="w-fit">
              <ChevronLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          )}
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 px-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                <ClipboardCheck className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Test Results Available</h3>
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                This candidate hasn't completed any assessments yet.
              </p>
            </CardContent>
          </Card>
        </div>
      </LayoutWrapper>
    );
  }

  // ── trait table row ───────────────────────────────────────────────────────
  const TraitRow = ({ key: k, label }) => {
    const score = d[k];
    if (typeof score !== 'number') return null;
    return (
      <TableRow className="hover:bg-muted/30">
        <TableCell className="font-medium">{label}</TableCell>
        <TableCell className="text-muted-foreground text-sm">{getDescription(score, k)}</TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-2">
            <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className={cn("h-full rounded-full", getScoreColor((score / 10) * 100))} style={{ width: `${(score / 10) * 100}%` }} />
            </div>
            <Text size="sm" className="font-semibold min-w-[2rem]">{score}/10</Text>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // ── Inline Cheating Evidence view (local-fallback path) ────────────────
  // Only fires when a parent did NOT pass `onOpenEvidence` — i.e. we're
  // on the standalone /reports route. The dashboard renders INSIDE this
  // Reports component's LayoutWrapper (EmployerLayout when standalone),
  // so the sidebar / header chrome stay visible while the body swaps.
  // Back inside the dashboard clears the state and falls through to the
  // main Reports return below — the recruiter sees their familiar
  // result summary again, never leaving the page they were on.
  if (localEvidenceTestId) {
    return (
      <LayoutWrapper>
        <Container size="default" className="py-6">
          <CheatingEvidence
            embedded
            testIdProp={localEvidenceTestId}
            typeProp={localEvidenceType}
            onClose={() => { setLocalEvidenceTestId(null); setLocalEvidenceType(null); }}
          />
        </Container>
      </LayoutWrapper>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <Container size="default" className="py-6">
        <Stack spacing="lg">

          {/* Header actions */}
          <Inline spacing="md" justify="between" align="center" className="flex-wrap">
            {!embedded ? (
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="w-fit">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            ) : (
              <span /> /* spacer so the Download button stays right-aligned via justify-between */
            )}
            <Button variant="default" onClick={handleBrowserPrint}
              style={{ backgroundColor: getColor('primary'), color: getColor('primaryForeground') }}>
              <Download className="w-4 h-4 mr-2" /> Download Report as PDF
            </Button>
          </Inline>

          {/* Report card */}
          <Card ref={reportRef}>
            {/* ── Header ── */}
            <CardHeader className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
              <div>
                <Heading level="h1" className="mb-2">{d.name}</Heading>
                <Heading level="h2" className="text-muted-foreground font-medium text-lg">
                  {jobDetails?.job_title}
                </Heading>
              </div>

              <div className="flex flex-col md:flex-row gap-4 items-center">
                {/* Rank */}
                <Card className="bg-secondary border-2 border-primary/30 p-4 rounded-lg text-center shadow-sm">
                  <Text size="sm" variant="muted" weight="semibold" className="mb-1 uppercase tracking-wide">Candidate Rank</Text>
                  <Heading level="h2" className="text-primary font-bold text-2xl">
                    <strong>{candidate.rank}</strong> / {candidate.total_results}
                  </Heading>
                </Card>

                {/* Score + fit */}
                <Stack spacing="xs" align="center" className="text-center">
                  <div className="w-20 h-20 bg-success-foreground text-success-text text-[22px] font-bold rounded-full flex justify-center items-center">
                    {roundedAverage}%
                  </div>
                  <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full border mt-1.5', FIT_BADGE[fitStatus])}>
                    {fitStatus}
                  </span>
                </Stack>

                {/* CTC / notice */}
                <Card className="min-w-[240px] bg-card border rounded-lg shadow-sm">
                  <Stack spacing="sm" className="p-4">
                    {[['Current CTC', `${d.cur_ctc} LPA`], ['Expected CTC', `${d.exp_ctc} LPA`], ['Notice Period', `${d.notice_period} days`]].map(([label, val]) => (
                      <Inline key={label} spacing="md" className="flex justify-between items-center pb-2 border-b border-dashed last:border-0 last:pb-0">
                        <Text size="sm" weight="semibold" variant="muted">{label}:</Text>
                        <Text size="sm">{val}</Text>
                      </Inline>
                    ))}
                  </Stack>
                </Card>
              </div>
            </CardHeader>

            <CardContent className="p-6">
              <Stack spacing="xl">

                {/* ── Overview ── */}
                <Stack spacing="sm">
                  <SectionDivider label="Overview" />
                  <Grid cols={2} gap="md" className="grid-cols-1 md:grid-cols-2">
                    <Card className="border">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" /> Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Text size="sm" className="leading-relaxed text-muted-foreground">{d.Overall_Summary}</Text>
                      </CardContent>
                    </Card>
                    <Card className="border">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-muted-foreground" /> Score Breakdown
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 p-4">
                        <CustomPieChart
                          scores={d}
                          noCard={true}
                          onCodingClick={hasCodingTest ? () => setCodingModalOpen(true) : undefined}
                          onScreeningClick={hasScreeningTest ? () => setScreeningModalOpen(true) : undefined}
                          onCommunicationClick={hasCommunicationTest ? () => setCommunicationModalOpen(true) : undefined}
                          onNonTechnicalClick={(hasPersonalityTest || hasBehavioralTest) ? () => setPnbModalOpen(true) : undefined}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Stack>

                {/* ── AI Scorecard ── */}
                <Stack spacing="sm">
                  <SectionDivider icon={<Sparkles className="w-3 h-3 mr-1.5" />} label="AI Interview Scorecard" />
                  {/* Responsive grid so cards keep a comfortable width no matter how
                      many tests were taken (5+ cards no longer squeeze into one row,
                      which wrapped labels to 3 lines and clipped the score numbers). */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                    {/* Evaluation metrics */}
                    {hasScreeningTest && (
                      <Card className="border min-w-0 w-full">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-muted-foreground" /> AI Evaluation Metrics
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <ScoreBar label="Overall AI Score"  value={roundedAverage}       />
                          <ScoreBar label="Screening Test"    value={d.screening_score}    />
                          <ScoreBar label="Domain Score"      value={d.domain_score}       />
                          <ScoreBar label="Confidence Level"  value={d.confidence_score}   />
                          <ScoreBar label="Resume Score"      value={d.Resume_Score}       />
                        </CardContent>
                      </Card>
                    )}

                    {/* Coding */}
                    {hasCodingTest && (
                      <Card className="border min-w-0 w-full">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Code2 className="w-4 h-4 text-muted-foreground" /> Coding Test Evaluation
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <ScoreBar label="Code Efficiency" value={d.efficiency}       />
                          <ScoreBar label="Performance"     value={d.performance}      />
                          <ScoreBar label="Robustness"      value={d.robustness}       />
                          <ScoreBar label="Readability"     value={d.readability_score}/>
                          <ScoreBar label="Correctness"     value={d.correctness}      />
                        </CardContent>
                      </Card>
                    )}

                    {/* Communication test */}
                    {hasCommunicationTest && (
                      <Card className="border min-w-0 w-full">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-muted-foreground" /> Communication Evaluation
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <ScoreBar label="Overall Score"        value={d.communication_score}      />
                          <ScoreBar label="Clarity"              value={(d.Clarity              ?? 0) * 10} />
                          <ScoreBar label="Grammar & Language"   value={(d.Grammar_Proficiency  ?? 0) * 10} />
                          <ScoreBar label="Tone & Professionalism" value={(d.Tone_and_Professionalism ?? 0) * 10} />
                          <ScoreBar label="Listening"            value={(d.Listening            ?? 0) * 10} />
                          <ScoreBar label="Persuasiveness"       value={(d.Persuasiveness       ?? 0) * 10} />
                        </CardContent>
                      </Card>
                    )}

                    {/* Aptitude test */}
                    {hasAptitudeTest && (
                      <Card className="border min-w-0 w-full">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Calculator className="w-4 h-4 text-muted-foreground" /> Aptitude Evaluation
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <ScoreBar label="Overall Score" value={d.aptitude_score} />
                          {d.aptitude_accuracy != null && (
                            <ScoreBar label="Accuracy" value={d.aptitude_accuracy} />
                          )}
                          {d.aptitude_voice_score != null && (
                            <ScoreBar label="Verbal Answers" value={d.aptitude_voice_score} />
                          )}
                          {d.aptitude_total_questions != null && (
                            <Text size="xs" variant="muted" className="pt-1">
                              {d.aptitude_correct_count ?? 0} correct of {d.aptitude_attempted_count ?? 0} attempted
                              ({d.aptitude_total_questions} questions total)
                            </Text>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2 gap-1.5"
                            onClick={() => setAptitudeModalOpen(true)}
                          >
                            <FileText className="w-3.5 h-3.5" /> View detailed report
                          </Button>
                        </CardContent>
                      </Card>
                    )}

                    {/* Audio / Visual */}
                    {hasScreeningTest && (
                      <Card className="border min-w-0 w-full">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <Mic className="w-4 h-4 text-muted-foreground" /> Audio / Visual Feedback
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <ScoreBar label="Pitch"       value={d.Pitch}        outOf={10} />
                          <ScoreBar label="Volume"      value={d.Volume}       outOf={10} />
                          <ScoreBar label="Speech Rate" value={d.rate}         outOf={10} />
                          <ScoreBar label="Pauses"      value={d.Pauses}       outOf={10} />
                          <ScoreBar label="Grammar"     value={d.Grammar_Score}outOf={10} />
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </Stack>

                {/* ── Assessment Insights ── */}
                {testInsights.length > 0 && (
                  <Stack spacing="sm">
                    <SectionDivider icon={<Brain className="w-3 h-3 mr-1.5" />} label="Assessment Insights" />
                    <div className="flex flex-wrap justify-center gap-4">
                      {/* Strengths panel */}
                      {testInsights.some(t => t.strengths.length) && (
                        <Card className="border w-full max-w-md">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-600" /> Strengths
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0 space-y-4">
                            {testInsights.filter(t => t.strengths.length).map((t, idx) => (
                              <div key={idx} className="space-y-2">
                                <div className="flex items-center gap-2 mb-2">
                                  <img src={t.icon} alt={t.title} className="w-5 h-5" />
                                  <Text size="sm" weight="semibold" className="text-muted-foreground">{t.title}</Text>
                                </div>
                                <ul className="list-none space-y-1.5">
                                  {t.strengths.map((pt, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                      <span className="text-green-600 mt-1.5 flex-shrink-0">•</span>
                                      <span>{pt}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}

                      {/* Weaknesses panel */}
                      {testInsights.some(t => t.weaknesses.length) && (
                        <Card className="border w-full max-w-md">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                              <XCircle className="w-4 h-4 text-red-600" /> Areas to Improve
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0 space-y-4">
                            {testInsights.filter(t => t.weaknesses.length).map((t, idx) => (
                              <div key={idx} className="space-y-2">
                                <div className="flex items-center gap-2 mb-2">
                                  <img src={t.icon} alt={t.title} className="w-5 h-5" />
                                  <Text size="sm" weight="semibold" className="text-muted-foreground">{t.title}</Text>
                                </div>
                                <ul className="list-none space-y-1.5">
                                  {t.weaknesses.map((pt, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                      <span className="text-red-600 mt-1.5 flex-shrink-0">•</span>
                                      <span>{pt}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </Stack>
                )}

                {/* ── Detailed Test Breakdown ── */}
                {(hasScreeningTest || hasNonTechnicalScores || hasCommunicationTraits || aptitudeSections.length > 0) && (
                  <Stack spacing="sm">
                    <SectionDivider icon={<BarChart3 className="w-3 h-3 mr-1.5" />} label="Detailed Test Breakdown" />
                    <div className="flex flex-col gap-6">

                      {/* Technical topics */}
                      {hasScreeningTest && formattedTopics.length > 0 && (
                        <TraitTable
                          icon={<Cpu className="w-4 h-4 text-muted-foreground" />}
                          heading="Technical Evaluation"
                          rows={formattedTopics.map(({ topic, score }) => ({
                            label: topic,
                            description: generateTopicDescription(topic, score),
                            score,
                            outOf: 10,
                          }))}
                        />
                      )}

                      {/* Personality + Behavioural */}
                      {hasNonTechnicalScores && (
                        <TraitTable
                          icon={<Brain className="w-4 h-4 text-muted-foreground" />}
                          heading="Non-Technical Evaluation"
                          rows={nonTechnicalScores.map(({ key, label }) => ({
                            label,
                            description: getDescription(d[key], key),
                            score: d[key],
                            outOf: 10,
                          }))}
                        />
                      )}

                      {/* Aptitude sections */}
                      {aptitudeSections.length > 0 && (
                        <TraitTable
                          icon={<Calculator className="w-4 h-4 text-muted-foreground" />}
                          heading="Aptitude — Section-wise Performance"
                          rows={aptitudeSections.map(({ label, score, attempted, correct, total }) => ({
                            label,
                            description: total != null
                              ? `${correct ?? 0} correct of ${attempted ?? 0} attempted (${total} questions)`
                              : '',
                            score: score != null ? Math.round(score) : null,
                            outOf: 100,
                          }))}
                        />
                      )}

                      {/* Communication traits */}
                      {hasCommunicationTraits && (
                        <TraitTable
                          icon={<MessageSquare className="w-4 h-4 text-muted-foreground" />}
                          heading="Communication Evaluation"
                          rows={communicationTraits
                            .filter(({ key }) => typeof d[key] === 'number')
                            .map(({ key, label }) => ({
                              label,
                              description: getDescription(d[key], key),
                              score: d[key],
                              outOf: 10,
                            }))}
                        />
                      )}
                    </div>
                  </Stack>
                )}

                {/* ── Deep Code Analysis ── */}
                {hasCodingTest && (
                  <Stack spacing="sm">
                    <SectionDivider icon={<Code2 className="w-3 h-3 mr-1.5" />} label="Deep Code Analysis" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                      {/* Plagiarism */}
                      {(hasCodingTestScheduled || hasPlagiarismData) && (
                        <PlagiarismCard
                          plagiarism={d.plagiarism_percentage}
                          codingTestId={base.coding_test_id || jobDetails?.coding_test_id || null}
                          // Forward the parent's inline-evidence
                          // opener (if any) so "View evidence →"
                          // opens the dashboard in-place rather than
                          // navigating away from this Reports view.
                          onOpenEvidence={onOpenEvidence}
                        />
                      )}

                      {/* Code cards */}
                      {[
                        { icon: <FileCode className="w-4 h-4 text-muted-foreground" />, title: 'Code Summary',            value: d.coding_summary },
                        { icon: <Building2 className="w-4 h-4 text-muted-foreground" />, title: 'Code Architecture',       value: d.code_architecture },
                        { icon: <Zap className="w-4 h-4 text-muted-foreground" />,       title: 'Performance Optimization', value: d.performance_optimization },
                        { icon: <Brain className="w-4 h-4 text-muted-foreground" />,     title: 'Algorithm Selection',      value: d.algorithm_selection },
                      ].map(({ icon, title, value }) => (
                        <BulletCard key={title} icon={icon} title={title} value={value} />
                      ))}
                    </div>
                  </Stack>
                )}

              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>

      {/* ── Coding Detail Modal ── */}
      {hasCodingTest && (
        <CodingDetailModal
          open={codingModalOpen}
          onClose={() => setCodingModalOpen(false)}
          d={d}
          codingTestId={base.coding_test_id || jobDetails?.coding_test_id || null}
          candidateName={d.name}
          jobTitle={jobDetails?.job_title}
        />
      )}

      {/* ── Screening Detail Modal ── */}
      {hasScreeningTest && (
        <ScreeningDetailModal
          open={screeningModalOpen}
          onClose={() => setScreeningModalOpen(false)}
          d={d}
          screeningTestId={base.screening_test_id || jobDetails?.screening_test_id || null}
          candidateName={d.name}
          jobTitle={jobDetails?.job_title}
          onOpenEvidence={onOpenEvidence}
        />
      )}

      {/* ── Aptitude Detail Modal ── */}
      {hasAptitudeTest && (
        <AptitudeDetailModal
          open={aptitudeModalOpen}
          onClose={() => setAptitudeModalOpen(false)}
          d={d}
          aptitudeTestId={base.aptitude_test_id || jobDetails?.aptitude_test_id || null}
          aptitudeSections={aptitudeSections}
          candidateName={d.name}
          jobTitle={jobDetails?.job_title}
        />
      )}

      {/* ── Communication Detail Modal ── */}
      {hasCommunicationTest && (
        <CommunicationDetailModal
          open={communicationModalOpen}
          onClose={() => setCommunicationModalOpen(false)}
          d={d}
          communicationTestId={base.communication_test_id || jobDetails?.communication_test_id || null}
          candidateName={d.name}
          jobTitle={jobDetails?.job_title}
          onOpenEvidence={onOpenEvidence}
        />
      )}

      {/* ── Personality & Behavioural Detail Modal (dynamic: P only / B only / both) ── */}
      {(hasPersonalityTest || hasBehavioralTest) && (
        <PnbDetailModal
          open={pnbModalOpen}
          onClose={() => setPnbModalOpen(false)}
          d={d}
          pnbTestId={base.all_test_id || base.personality_test_id || base.behavioral_test_id
            || jobDetails?.all_test_id || jobDetails?.personality_test_id || jobDetails?.behavioral_test_id || null}
          candidateName={d.name}
          jobTitle={jobDetails?.job_title}
          onOpenEvidence={onOpenEvidence}
        />
      )}

    </LayoutWrapper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Aptitude Detail Modal — per-question report card
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Personality & Behavioural Detail Modal — dynamic: shows whichever dimension(s)
// were actually scored (personality-only, behavioural-only, or both) + transcript
// ─────────────────────────────────────────────────────────────────────────────

// Reusable "Cheating evidence" header button. Opens the proctoring Evidence
// Dashboard (webcam snapshots, gaze/face/head-pose events, lockdown flags) for
// a given test id. Mirrors the aptitude report's button so every detailed
// report reads alike. Renders nothing when there is no usable test id, so it is
// safe to drop into any modal header. `screening_test_id` (and friends) can be
// a comma-separated list from the JD; we resolve to the last non-empty id,
// matching the "always show the latest test" convention used elsewhere.
function ProctoringEvidenceButton({ testId, type = null, onOpenEvidence = null, onCloseModal = null }) {
  const navigate = useNavigate();
  const evidenceTid = String(testId || '')
    .split(',').map(s => s.trim()).filter(Boolean).pop() || null;
  if (!evidenceTid) return null;
  const handleClick = () => {
    // Prefer the inline open (exactly like the coding "View evidence" link):
    // close this modal, then swap the Reports body for the embedded dashboard
    // so the recruiter stays inside the same shell. Fall back to a full-page
    // route only if no inline opener was wired.
    if (typeof onOpenEvidence === 'function') {
      if (typeof onCloseModal === 'function') onCloseModal();
      onOpenEvidence(evidenceTid, type);
    } else {
      navigate(`/hirewithus/cheating-evidence/${encodeURIComponent(evidenceTid)}${type ? `?type=${encodeURIComponent(type)}` : ''}`);
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5 flex-shrink-0 text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-950/30"
      onClick={handleClick}
      title="View proctoring evidence — webcam snapshots and detector events for this test"
    >
      <ShieldAlert className="w-3.5 h-3.5" /> Cheating evidence
    </Button>
  );
}

function PnbDetailModal({ open, onClose, d, pnbTestId, candidateName, jobTitle, onOpenEvidence = null }) {
  const [conversation, setConversation] = useState([]);
  const [convLoading, setConvLoading]   = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!pnbTestId) { setConversation([]); return; }
    setConvLoading(true);
    fetch(`${API_BASE_URL}/api/screening_conversation?test_id=${encodeURIComponent(pnbTestId)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setConversation(Array.isArray(data?.conversation) ? data.conversation : []))
      .catch(() => setConversation([]))
      .finally(() => setConvLoading(false));
  }, [open, pnbTestId]);

  const personalityTraits = [
    { key: 'Introversion_vs_Extroversion', label: 'Extroversion'          },
    { key: 'Emotional_Stability',          label: 'Emotional Stability'    },
    { key: 'Openness_to_Experience',       label: 'Openness to Experience' },
    { key: 'Agreeableness',                label: 'Agreeableness'          },
    { key: 'Conscientiousness',            label: 'Conscientiousness'      },
  ].filter(t => typeof d[t.key] === 'number');
  const behavioralTraits = [
    { key: 'Problem_Solving',              label: 'Problem Solving'            },
    { key: 'Communication_Skills',         label: 'Communication Skills'       },
    { key: 'Handling_Pressure',            label: 'Handling Pressure'          },
    { key: 'Ownership_and_Responsibility', label: 'Ownership & Responsibility' },
    { key: 'Values_and_Ethics',            label: 'Values and Ethics'          },
  ].filter(t => typeof d[t.key] === 'number');

  const hasP = d.personality_score != null && d.personality_score > 0;
  const hasB = d.behavioral_score  != null && d.behavioral_score  > 0;
  const title = hasP && hasB ? 'Personality & Behavioural' : hasP ? 'Personality' : hasB ? 'Behavioural' : 'Personality & Behavioural';

  const renderSection = (heading, score, traits, barColor) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">{heading}</h4>
        {score != null && <span className="text-sm font-bold">{Math.round(score)}%</span>}
      </div>
      <div className="space-y-1">
        {traits.map(t => (
          <div key={t.key} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
            <span className="text-sm font-medium min-w-0">{t.label}</span>
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full', barColor)} style={{ width: `${Math.min(100, (Number(d[t.key]) / 10) * 100)}%` }} />
              </div>
              <span className="text-sm font-semibold min-w-[3rem] text-right">{Number(d[t.key])}/10</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">

        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
            <Brain className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold leading-none truncate">{title} — Detailed Report</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground mt-0.5 truncate">
              {candidateName}{jobTitle ? ` · ${jobTitle}` : ''}
            </DialogDescription>
          </div>
          <ProctoringEvidenceButton testId={pnbTestId} type="pnb" onOpenEvidence={onOpenEvidence} onCloseModal={onClose} />
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {hasP && personalityTraits.length > 0 &&
            renderSection('Personality (Big Five)', d.personality_score, personalityTraits, 'bg-amber-500')}
          {hasB && behavioralTraits.length > 0 &&
            renderSection('Behavioural competencies', d.behavioral_score, behavioralTraits, 'bg-orange-500')}

          {/* Interview transcript */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Interview transcript</h4>
            {convLoading ? (
              <p className="text-sm text-muted-foreground">Loading transcript…</p>
            ) : conversation.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transcript available for this interview.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto rounded-lg border border-border/60 p-3 bg-muted/20">
                {conversation.map((m, i) => {
                  const isUser = String(m.role || '').toLowerCase() === 'user';
                  return (
                    <div key={i} className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
                      <div className={cn(
                        'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                        isUser ? 'bg-amber-100 dark:bg-amber-900/30 text-foreground' : 'bg-background border border-border/60',
                      )}>
                        <span className="block text-[10px] font-semibold mb-0.5 text-muted-foreground">
                          {isUser ? (candidateName || 'Candidate') : 'Sana'}
                        </span>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Communication Detail Modal — skill breakdown + strengths / weaknesses
// ─────────────────────────────────────────────────────────────────────────────

function CommunicationDetailModal({ open, onClose, d, communicationTestId, candidateName, jobTitle, onOpenEvidence = null }) {
  const [conversation, setConversation] = useState([]);
  const [convLoading, setConvLoading]   = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!communicationTestId) { setConversation([]); return; }
    setConvLoading(true);
    // Same generic endpoint the screening report uses — reads the transcript
    // for any test_id (handles the JD's comma-separated batches).
    fetch(`${API_BASE_URL}/api/screening_conversation?test_id=${encodeURIComponent(communicationTestId)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setConversation(Array.isArray(data?.conversation) ? data.conversation : []))
      .catch(() => setConversation([]))
      .finally(() => setConvLoading(false));
  }, [open, communicationTestId]);

  const traits = [
    { label: 'Clarity',                value: d.Clarity },
    { label: 'Grammar & Language',     value: d.Grammar_Proficiency },
    { label: 'Tone & Professionalism', value: d.Tone_and_Professionalism },
    { label: 'Listening',              value: d.Listening },
    { label: 'Persuasiveness',         value: d.Persuasiveness },
  ].filter(t => typeof t.value === 'number');

  const strength = d.Communication_Strength;
  const weakness = d.Communication_Weakness;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">

        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold leading-none truncate">Communication — Detailed Report</DialogTitle>
            <DialogDescription className="text-[12px] text-muted-foreground mt-0.5 truncate">
              {candidateName}{jobTitle ? ` · ${jobTitle}` : ''}
            </DialogDescription>
          </div>
          <ProctoringEvidenceButton testId={communicationTestId} type="communication" onOpenEvidence={onOpenEvidence} onCloseModal={onClose} />
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Overall */}
          <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-4 py-3">
            <span className="text-sm font-medium text-muted-foreground">Overall communication score</span>
            <span className="text-2xl font-bold">
              {d.communication_score != null ? `${Math.round(d.communication_score)}%` : '—'}
            </span>
          </div>

          {/* Skill breakdown */}
          {traits.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Skill breakdown</h4>
              <div className="space-y-1">
                {traits.map(t => (
                  <div key={t.label} className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors">
                    <span className="text-sm font-medium min-w-0">{t.label}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (Number(t.value) / 10) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-semibold min-w-[3rem] text-right">{Number(t.value)}/10</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strengths / Weaknesses */}
          {(strength || weakness) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {strength && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/20 p-3">
                  <h5 className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Strengths</h5>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{strength}</p>
                </div>
              )}
              {weakness && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20 p-3">
                  <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Areas to improve</h5>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{weakness}</p>
                </div>
              )}
            </div>
          )}

          {/* Interview transcript */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Interview transcript</h4>
            {convLoading ? (
              <p className="text-sm text-muted-foreground">Loading transcript…</p>
            ) : conversation.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transcript available for this interview.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto rounded-lg border border-border/60 p-3 bg-muted/20">
                {conversation.map((m, i) => {
                  const isUser = String(m.role || '').toLowerCase() === 'user';
                  return (
                    <div key={i} className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
                      <div className={cn(
                        'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                        isUser
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-foreground'
                          : 'bg-background border border-border/60',
                      )}>
                        <span className="block text-[10px] font-semibold mb-0.5 text-muted-foreground">
                          {isUser ? (candidateName || 'Candidate') : 'Sana'}
                        </span>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AptitudeDetailModal({ open, onClose, d, aptitudeTestId, aptitudeSections, candidateName, jobTitle }) {
  const printRef = useRef();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [sectionFilter, setSectionFilter] = useState('all');
  // The backend resolves which id from a comma-separated list actually has
  // results — use that same id for the cheating-evidence link.
  const [resolvedTid, setResolvedTid] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (!aptitudeTestId) { setQuestions([]); return; }
    setLoading(true);
    fetch(`${API_BASE_URL}/api/aptitude_submission_results?test_id=${encodeURIComponent(aptitudeTestId)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setQuestions(Array.isArray(data?.questions) ? data.questions : []);
        setSummary(data?.summary || null);
        setResolvedTid(data?.test_id || null);
      })
      .catch(() => { setQuestions([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [open, aptitudeTestId]);

  const evidenceTid = resolvedTid
    || String(aptitudeTestId || '').split(',').map(s => s.trim()).filter(Boolean).pop()
    || null;

  const handleDownload = () => {
    if (!printRef.current) return;
    const pw = window.open('', '_blank');
    if (!pw) {
      toast.error('Pop-up blocked', { description: 'Allow pop-ups to download the aptitude report.' });
      return;
    }
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.href).filter(Boolean);
    const inlineStyles = Array.from(document.querySelectorAll('style')).map(s => s.innerHTML).join('\n');
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Aptitude Report — ${candidateName}</title>
      ${stylesheets.map(h => `<link rel="stylesheet" href="${h}">`).join('')}
      <style>${inlineStyles}@media print{*{-webkit-print-color-adjust:exact!important}body{margin:0;padding:20px}}</style>
    </head><body>${printRef.current.innerHTML}</body></html>`);
    pw.document.close();
    setTimeout(() => { pw.print(); pw.close(); }, 500);
  };

  const sectionKeys = [...new Set(questions.map(q => q.section))];
  const sectionLabel = (key) =>
    (aptitudeSections || []).find(s => s.key === key)?.label ||
    key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const visible = sectionFilter === 'all' ? questions : questions.filter(q => q.section === sectionFilter);

  const fmtSecs = (s) => {
    if (s == null) return null;
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  };

  const summaryChips = [
    { label: 'Overall',   value: d.aptitude_score       != null ? `${Math.round(d.aptitude_score)}%`       : '—' },
    { label: 'Accuracy',  value: d.aptitude_accuracy    != null ? `${Math.round(d.aptitude_accuracy)}%`    : '—' },
    { label: 'Attempted', value: d.aptitude_total_questions != null ? `${d.aptitude_attempted_count ?? 0}/${d.aptitude_total_questions}` : '—' },
    ...(d.aptitude_voice_score != null ? [{ label: 'Verbal', value: `${Math.round(d.aptitude_voice_score)}%` }] : []),
  ];

  // Response-time anomaly analysis (advisory — never affects the score)
  const timing = (() => {
    let t = summary?.timing_analysis;
    if (typeof t === 'string') { try { t = JSON.parse(t); } catch { t = null; } }
    return t && typeof t === 'object' ? t : null;
  })();

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0">

        {/* ── Sticky header ── */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0">
              <Calculator className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-foreground truncate">Aptitude Report — {candidateName}</h2>
              <p className="text-xs text-muted-foreground truncate">{jobTitle || 'Aptitude Test'} · Test ID {aptitudeTestId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {evidenceTid && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-950/30"
                onClick={() => navigate(`/hirewithus/cheating-evidence/${encodeURIComponent(evidenceTid)}?type=apt`)}
              >
                <ShieldAlert className="w-3.5 h-3.5" /> Cheating evidence
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> Download
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div ref={printRef} className="px-6 py-5 flex flex-col gap-5">

          {/* Summary chips */}
          <div className="flex flex-wrap gap-3">
            {summaryChips.map(({ label, value }) => (
              <div key={label} className="px-4 py-2 rounded-lg border border-border bg-muted/40">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-lg font-bold tabular-nums">{value}</p>
              </div>
            ))}
            {(aptitudeSections || []).map((s) => (
              <div key={s.key} className="px-4 py-2 rounded-lg border border-border">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
                <p className="text-lg font-bold tabular-nums">
                  {s.score != null ? `${Math.round(s.score)}%` : '—'}
                  <span className="text-xs font-normal text-muted-foreground ml-1.5">
                    {s.correct ?? 0}/{s.total ?? 0}
                  </span>
                </p>
              </div>
            ))}
            {timing?.avg_seconds_per_question != null && (
              <div className="px-4 py-2 rounded-lg border border-border">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Avg time / question</p>
                <p className="text-lg font-bold tabular-nums">{timing.avg_seconds_per_question}s</p>
              </div>
            )}
          </div>

          {/* Response-time anomaly banner */}
          {timing?.fast_correct_count > 0 && (
            <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-3">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {timing.fast_correct_count} correct answer{timing.fast_correct_count === 1 ? '' : 's'} faster than plausible reading time
                {timing.fast_correct_questions?.length ? ` (Q${timing.fast_correct_questions.join(', Q')})` : ''}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Possible pre-knowledge of these questions. Advisory only — the score is unaffected.
                Review the flagged questions below and the screen recording before drawing conclusions.
              </p>
            </div>
          )}

          {/* Section filter */}
          {sectionKeys.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant={sectionFilter === 'all' ? 'default' : 'outline'}
                size="sm" className="h-7 text-xs"
                onClick={() => setSectionFilter('all')}
              >
                All ({questions.length})
              </Button>
              {sectionKeys.map((key) => (
                <Button
                  key={key}
                  variant={sectionFilter === key ? 'default' : 'outline'}
                  size="sm" className="h-7 text-xs"
                  onClick={() => setSectionFilter(key)}
                >
                  {sectionLabel(key)} ({questions.filter(q => q.section === key).length})
                </Button>
              ))}
            </div>
          )}

          {/* Per-question cards */}
          {loading ? (
            <div className="flex items-center gap-2 justify-center py-10 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading question details…
            </div>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              No per-question details available for this test.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {visible.map((q) => {
                const isVoice = q.question_type === 'voice';
                const isOpenEnded = q.question_type !== 'mcq'; // voice + written
                const verdict = q.is_correct === true ? 'correct' : q.is_correct === false ? 'wrong' : 'none';
                return (
                  <div
                    key={q.question_number}
                    className={cn(
                      'rounded-lg border p-4',
                      verdict === 'correct' && 'border-green-300 dark:border-green-800',
                      verdict === 'wrong' && 'border-red-300 dark:border-red-800',
                      verdict === 'none' && 'border-border',
                    )}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge variant="outline" className="text-[10px]">Q{q.question_number}</Badge>
                      <Badge variant="secondary" className="text-[10px]">{sectionLabel(q.section)}</Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {isVoice ? <><Mic className="w-2.5 h-2.5 mr-1" />Verbal</> : isOpenEnded ? 'Written' : 'MCQ'}
                      </Badge>
                      {isVoice && q.answered_via === 'typed' && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700">
                          Typed (mic fallback)
                        </Badge>
                      )}
                      {q.source && q.source !== 'ai' && (
                        <Badge variant="outline" className="text-[10px]">
                          {q.source === 'company' ? 'Company question' : 'Custom question'}
                        </Badge>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {q.timing_flag === 'too_fast' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            <AlertTriangle className="w-3 h-3" /> Suspiciously fast
                          </span>
                        )}
                        {q.timing_flag === 'rapid_guess' && (
                          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            Rapid guess
                          </span>
                        )}
                        {q.time_taken_seconds != null && (
                          <span className="text-xs text-muted-foreground tabular-nums">{fmtSecs(q.time_taken_seconds)}</span>
                        )}
                        {verdict === 'correct' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                        {verdict === 'wrong' && <XCircle className="w-4 h-4 text-red-600" />}
                        {verdict === 'none' && !isOpenEnded && (
                          <span className="text-[10px] uppercase text-muted-foreground">Not attempted</span>
                        )}
                        {verdict === 'none' && isOpenEnded && q.ai_score == null && (
                          <span className="text-[10px] uppercase text-muted-foreground">Not graded</span>
                        )}
                      </span>
                    </div>

                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap mb-3">{q.question}</p>

                    {!isOpenEnded ? (
                      <div className="space-y-1.5">
                        {(q.options || []).map((opt, oi) => {
                          const isCorrectOpt = opt === q.correct_answer;
                          const isChosen = opt === q.chosen_answer;
                          return (
                            <div
                              key={oi}
                              className={cn(
                                'flex items-center gap-2 px-3 py-1.5 rounded text-sm border',
                                isCorrectOpt && 'bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-800 text-green-800 dark:text-green-300',
                                isChosen && !isCorrectOpt && 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800 text-red-800 dark:text-red-300',
                                !isCorrectOpt && !isChosen && 'border-transparent text-muted-foreground',
                              )}
                            >
                              <span className="font-mono text-xs w-4">{String.fromCharCode(65 + oi)}.</span>
                              <span className="flex-1">{opt}</span>
                              {isCorrectOpt && <span className="text-[10px] uppercase font-semibold">Correct</span>}
                              {isChosen && !isCorrectOpt && <span className="text-[10px] uppercase font-semibold">Chosen</span>}
                              {isChosen && isCorrectOpt && <span className="text-[10px] uppercase font-semibold">Chosen ✓</span>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="rounded-md bg-muted/40 border border-border px-3 py-2">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                            {isVoice
                              ? (q.answered_via === 'typed'
                                  ? "Candidate's typed answer (microphone fallback)"
                                  : "Candidate's spoken answer (transcript)")
                              : "Candidate's written answer"}
                          </p>
                          <p className="text-sm leading-relaxed">{q.transcript || <span className="text-muted-foreground italic">No answer recorded</span>}</p>
                        </div>
                        <div className="flex flex-wrap items-start gap-3">
                          {q.ai_score != null && (
                            <Badge variant={q.ai_score >= 6 ? 'default' : 'destructive'} className="text-xs">
                              AI score: {q.ai_score}/10
                            </Badge>
                          )}
                          {q.ai_feedback && (
                            <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">{q.ai_feedback}</p>
                          )}
                        </div>
                        {q.correct_answer && (
                          <p className="text-xs text-muted-foreground">
                            <span className="font-semibold">Expected answer:</span> {q.correct_answer}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Screening Detail Modal
// ─────────────────────────────────────────────────────────────────────────────

function ScreeningDetailModal({ open, onClose, d, screeningTestId, candidateName, jobTitle, onOpenEvidence = null }) {
  const printRef = useRef();
  const [conversation, setConversation] = useState([]);
  const [convLoading, setConvLoading]   = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!screeningTestId) { setConversation([]); return; }
    setConvLoading(true);
    fetch(`${API_BASE_URL}/api/screening_conversation?test_id=${encodeURIComponent(screeningTestId)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => setConversation(Array.isArray(data?.conversation) ? data.conversation : []))
      .catch(() => setConversation([]))
      .finally(() => setConvLoading(false));
  }, [open, screeningTestId]);

  const handleDownload = () => {
    if (!printRef.current) return;
    const pw = window.open('', '_blank');
    if (!pw) {
      toast.error('Pop-up blocked', { description: 'Allow pop-ups to download the screening report.' });
      return;
    }
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.href).filter(Boolean);
    const inlineStyles = Array.from(document.querySelectorAll('style')).map(s => s.innerHTML).join('\n');
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Screening Report — ${candidateName}</title>
      ${stylesheets.map(h => `<link rel="stylesheet" href="${h}">`).join('')}
      <style>${inlineStyles}@media print{*{-webkit-print-color-adjust:exact!important}body{margin:0;padding:20px}}</style>
    </head><body>${printRef.current.innerHTML}</body></html>`);
    pw.document.close();
    setTimeout(() => { pw.print(); pw.close(); }, 500);
  };

  // Topic scores
  let topicRows = [];
  try {
    const topicsArr   = typeof d.topics        === 'string' ? JSON.parse(d.topics)        : (d.topics        || []);
    const scoresArr   = typeof d.topics_scores === 'string' ? JSON.parse(d.topics_scores) : (d.topics_scores || []);
    topicRows = topicsArr.map((t, i) => ({ topic: t, score: scoresArr[i] ?? null })).filter(r => r.score != null);
  } catch (_) { topicRows = []; }

  const audioMetrics = [
    { label: 'Pitch',       value: d.Pitch,         outOf: 10 },
    { label: 'Volume',      value: d.Volume,        outOf: 10 },
    { label: 'Speech Rate', value: d.rate,          outOf: 10 },
    { label: 'Pauses',      value: d.Pauses,        outOf: 10 },
    { label: 'Grammar',     value: d.Grammar_Score, outOf: 10 },
  ];

  const scoreMetrics = [
    { label: 'Overall Screening Score', value: d.screening_score   },
    { label: 'Domain Knowledge',        value: d.domain_score      },
    { label: 'Confidence Level',        value: d.confidence_score  },
  ];

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">

        {/* ── Sticky header ── */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
              <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold leading-none truncate">Screening Test — Detailed Report</DialogTitle>
              <DialogDescription className="text-[12px] text-muted-foreground mt-0.5 truncate">
                {candidateName}{jobTitle ? ` · ${jobTitle}` : ''}
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ProctoringEvidenceButton testId={screeningTestId} type="screening" onOpenEvidence={onOpenEvidence} onCloseModal={onClose} />
            <Button size="sm" variant="outline" onClick={handleDownload} className="gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" />
              Download
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onClose}
              className="h-8 w-8 p-0"
              aria-label="Close report"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ── Printable body ── */}
        <div ref={printRef} className="px-6 py-5 space-y-6">

          {/* Candidate info header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-foreground">{candidateName}</p>
              {jobTitle && <p className="text-sm text-muted-foreground">{jobTitle}</p>}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: '#3f51b5' }}>{Math.round(d.screening_score ?? 0)}%</p>
              <p className="text-xs text-muted-foreground">Overall Screening Score</p>
            </div>
          </div>

          {/* ── Score metrics ── */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />Score Overview
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scoreMetrics.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border/40">
                  <span className="text-sm font-medium">{label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={cn('h-full rounded-full', getScoreColor(value ?? 0))} style={{ width: `${Math.min(100, value ?? 0)}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums w-10 text-right">{Math.round(value ?? 0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Audio / Speech Metrics ── */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Mic className="w-3.5 h-3.5" />Audio &amp; Speech Feedback
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {audioMetrics.map(({ label, value, outOf }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border/40">
                  <span className="text-sm font-medium">{label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={cn('h-full rounded-full', getScoreColor(((value ?? 0) / outOf) * 100))} style={{ width: `${Math.min(100, ((value ?? 0) / outOf) * 100)}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums w-10 text-right">{value ?? 0}/{outOf}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Topic-wise Breakdown ── */}
          {topicRows.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Cpu className="w-3.5 h-3.5" />Technical Topic Scores
              </p>
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Topic</th>
                      <th className="text-right px-4 py-2 font-semibold text-muted-foreground">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topicRows.map(({ topic, score }, i) => (
                      <tr key={i} className={cn('border-t border-border/30', i % 2 === 0 ? 'bg-background' : 'bg-muted/10')}>
                        <td className="px-4 py-2 font-medium">{topic}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className={cn('h-full rounded-full', getScoreColor((score / 10) * 100))} style={{ width: `${Math.min(100, (score / 10) * 100)}%` }} />
                            </div>
                            <span className="font-semibold tabular-nums">{score}/10</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Strengths / Weaknesses ── */}
          {(d.Screening_Strength || d.Screening_Weakness) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {d.Screening_Strength && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-3">
                  <p className="text-[11px] font-semibold text-green-700 flex items-center gap-1.5 mb-2">
                    <CheckCircle2 className="w-3.5 h-3.5" />Strengths
                  </p>
                  <ul className="space-y-1">
                    {String(d.Screening_Strength).replace(/[\[\]']+/g, '').split(',').map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-green-800">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.Screening_Weakness && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <p className="text-[11px] font-semibold text-red-700 flex items-center gap-1.5 mb-2">
                    <XCircle className="w-3.5 h-3.5" />Areas to Improve
                  </p>
                  <ul className="space-y-1">
                    {String(d.Screening_Weakness).replace(/[\[\]']+/g, '').split(',').map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-red-800">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Highlights ── */}
          {d.Screening_Highlights && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
              <p className="text-[11px] font-semibold text-blue-700 flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5" />AI Highlights
              </p>
              <p className="text-xs text-blue-900 leading-relaxed">{d.Screening_Highlights}</p>
            </div>
          )}

          {/* ── Interview Conversation ── */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />Interview Conversation
            </p>
            {convLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="w-4 h-4 animate-spin" />Loading conversation…
              </div>
            ) : conversation.length > 0 ? (
              <div className="space-y-2">
                {conversation.map((msg, i) => {
                  const isAI = (msg.role || '').toLowerCase() === 'assistant';
                  return (
                    <div key={i} className={cn('flex gap-2.5', isAI ? 'justify-start' : 'justify-end')}>
                      {isAI && (
                        <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Mic className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                      )}
                      <div className={cn(
                        'max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                        isAI
                          ? 'bg-muted/60 text-foreground rounded-tl-sm'
                          : 'bg-blue-600 text-white rounded-tr-sm',
                      )}>
                        <p className={cn('text-[10px] font-semibold mb-0.5', isAI ? 'text-muted-foreground' : 'text-blue-100')}>
                          {isAI ? 'AI Interviewer' : 'Candidate'}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.text || msg.message || ''}</p>
                      </div>
                      {!isAI && (
                        <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 px-4 py-6 text-center">
                <MessageSquare className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground font-medium">Conversation not available</p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  Conversation files are stored temporarily and may have been cleared.
                </p>
              </div>
            )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coding Detail Modal
// ─────────────────────────────────────────────────────────────────────────────

const DIFF_COLOR = {
  Easy:   'bg-green-100 text-green-700 border-green-200',
  Medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Hard:   'bg-red-100 text-red-700 border-red-200',
};

// Voice follow-up (code-comprehension) verdict styling — mirrors the
// Cheating-Evidence "Code Understanding" tab so the two surfaces read alike.
const FU_VERDICT = {
  passed:    { label: 'Passed',    cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900' },
  concerned: { label: 'Concerned', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900' },
  failed:    { label: 'Failed',    cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900' },
};
const FU_ANSWER_CLR = {
  correct:   'text-green-700 dark:text-green-400',
  partial:   'text-yellow-700 dark:text-yellow-400',
  wrong:     'text-red-600 dark:text-red-400',
  evasive:   'text-red-600 dark:text-red-400',
  no_answer: 'text-muted-foreground',
};

/**
 * Code Comprehension — the per-question voice follow-up result, shown inside
 * the coding report (also lives in the Cheating-Evidence page). Rendered only
 * when a follow-up session exists for this question; otherwise nothing.
 */
function FollowupComprehension({ fs }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const verdict    = FU_VERDICT[fs?.overall_verdict] || null;
  const score      = typeof fs?.comprehension_score === 'number' ? fs.comprehension_score : null;
  const qs         = Array.isArray(fs?.questions) ? fs.questions : [];
  const transcript = Array.isArray(fs?.transcript_inline) ? fs.transcript_inline : [];
  const micDenied  = fs?.mic_granted === false;
  const scoreColor = score == null ? 'bg-muted-foreground'
    : score >= 60 ? 'bg-green-500' : score >= 30 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div>
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
        <Mic className="w-3 h-3" />
        Code Comprehension
        <span className="text-[10px] normal-case font-normal text-muted-foreground/60">— voice check of code authorship</span>
      </p>
      <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-3 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {verdict && (
            <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border', verdict.cls)}>
              {verdict.label}
            </span>
          )}
          {score != null && (
            <div className="flex items-center gap-2 flex-1 min-w-[150px]">
              <span className="text-[11px] text-muted-foreground">Comprehension</span>
              <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden max-w-[160px]">
                <div className={cn('h-full', scoreColor)} style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
              </div>
              <span className="text-[11px] font-semibold tabular-nums text-foreground">{score}/100</span>
            </div>
          )}
          {micDenied && (
            <span className="text-[10px] font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />Mic denied{fs?.mic_denied_reason ? ` (${fs.mic_denied_reason})` : ''}
            </span>
          )}
        </div>

        {qs.length > 0 && (
          <div className="space-y-2">
            {qs.map((q, qi) => (
              <div key={qi} className="text-xs">
                <div className="flex items-start gap-1.5">
                  <span className="text-muted-foreground font-mono shrink-0">Q{q.idx ?? qi + 1}</span>
                  <span className="text-foreground flex-1">{q.text || '—'}</span>
                  <span className={cn('font-semibold capitalize shrink-0', FU_ANSWER_CLR[q.verdict] || 'text-muted-foreground')}>
                    {String(q.verdict || 'no_answer').replace('_', ' ')}
                  </span>
                </div>
                {q.evidence && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 ml-6 italic">{q.evidence}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {transcript.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowTranscript(v => !v)}
              className="text-[11px] font-semibold text-violet-600 hover:text-violet-700 hover:underline focus:outline-none"
            >
              {showTranscript ? 'Hide transcript' : `Show transcript (${transcript.length} lines)`}
            </button>
            {showTranscript && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto rounded-md bg-background border border-border/40 px-2.5 py-2">
                {transcript.map((t, tli) => (
                  <p key={tli} className="text-[11px] leading-snug">
                    <span className={cn('font-semibold', t.role === 'assistant' ? 'text-violet-600' : 'text-foreground')}>
                      {t.role === 'assistant' ? 'AI' : 'Candidate'}:
                    </span>{' '}
                    <span className="text-muted-foreground">{t.text}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionAccordion({ question, index, forceOpen = false }) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  const diff = question.difficulty || 'Medium';

  // Submission-level data (available when candidate submitted)
  const hasSubmission  = !!question.code_submitted;
  const testResults    = question.test_results  || [];   // [{input,expected,output,passed,error}]
  const passedCount    = question.passed_count  ?? null;
  const totalCount     = question.total_count   ?? null;
  const hasResults     = testResults.length > 0;
  // Fallback: plain test_cases from questions file (no output/passed info)
  const plainTestCases = question.test_cases    || [];

  const passAllBadge = passedCount != null && totalCount != null;
  const allPassed    = passedCount === totalCount && totalCount > 0;

  return (
    <div className="rounded-lg border border-border/60 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/60 transition-colors text-left"
      >
        <span className="w-6 h-6 rounded-md bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center text-[11px] font-bold text-violet-700 dark:text-violet-300 flex-shrink-0">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-tight truncate">
            {question.title || `Question ${index + 1}`}
          </p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {question.topic && (
              <span className="text-[11px] text-muted-foreground">{question.topic}</span>
            )}
            <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded border', DIFF_COLOR[diff] || DIFF_COLOR.Medium)}>
              {diff}
            </span>
            {question.time_minutes && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <Zap className="w-2.5 h-2.5" />{question.time_minutes} min
              </span>
            )}
          </div>
        </div>

        {/* Pass / fail badge */}
        {passAllBadge && (
          <span className={cn(
            'text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 flex items-center gap-1',
            allPassed
              ? 'bg-green-50 text-green-700 border-green-200'
              : passedCount === 0
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-yellow-50 text-yellow-700 border-yellow-200',
          )}>
            {allPassed ? <CheckCircle2 className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
            {passedCount}/{totalCount} passed
          </span>
        )}

        {isOpen
          ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>

      {/* Body */}
      {isOpen && (
        <div className="px-4 py-3 space-y-4 border-t border-border/40">

          {/* Description */}
          {question.description && (
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Problem Description</p>
              <p className="text-sm text-foreground leading-relaxed bg-muted/20 rounded-lg px-3 py-2.5 whitespace-pre-wrap">
                {question.description}
              </p>
            </div>
          )}

          {/* Candidate's submitted code */}
          {hasSubmission ? (
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <Code2 className="w-3 h-3" />
                Candidate's Code
                {question.language && (
                  <span className="text-[10px] font-normal normal-case text-muted-foreground/70">({question.language})</span>
                )}
              </p>
              <pre className={cn(
                "text-xs font-mono bg-zinc-950 text-zinc-100 rounded-lg px-4 py-3 leading-relaxed whitespace-pre-wrap break-words",
                forceOpen
                  ? "overflow-visible"                          // print: full code, no scroll
                  : "overflow-x-auto max-h-56 overflow-y-auto" // screen: scrollable box
              )}>
                {question.code_submitted || '// No code submitted'}
              </pre>
            </div>
          ) : (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />Candidate did not submit code for this question
            </p>
          )}

          {/* Test Results (with actual output) */}
          {hasResults ? (
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <FlaskConical className="w-3 h-3" />
                Test Results ({passedCount}/{totalCount} passed)
              </p>
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-8">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Input</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Expected</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Candidate Output</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground w-16">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((tc, ti) => (
                      <tr key={ti} className={cn('border-t border-border/30', ti % 2 === 0 ? 'bg-background' : 'bg-muted/10')}>
                        <td className="px-3 py-2 text-muted-foreground font-mono">{ti + 1}</td>
                        <td className="px-3 py-2 font-mono text-foreground whitespace-pre-wrap break-all max-w-[120px]">{tc.input != null ? (typeof tc.input === 'object' ? JSON.stringify(tc.input) : String(tc.input)) : '—'}</td>
                        <td className="px-3 py-2 font-mono text-foreground whitespace-pre-wrap break-all max-w-[120px]">{tc.expected != null ? (typeof tc.expected === 'object' ? JSON.stringify(tc.expected) : String(tc.expected)) : '—'}</td>
                        <td className={cn('px-3 py-2 font-mono whitespace-pre-wrap break-all max-w-[140px]',
                          tc.error ? 'text-red-500' : tc.passed ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          {tc.error ? `Error: ${tc.error}` : tc.output != null ? (typeof tc.output === 'object' ? JSON.stringify(tc.output) : String(tc.output)) : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {tc.passed
                            ? <CheckCircle2 className="w-4 h-4 text-green-600 mx-auto" />
                            : <XCircle     className="w-4 h-4 text-red-500   mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : plainTestCases.length > 0 ? (
            /* Fallback: show plain test cases (no submission data) */
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <FlaskConical className="w-3 h-3" />Test Cases ({plainTestCases.length})
                <span className="text-[10px] normal-case font-normal text-muted-foreground/60">— submission results not available</span>
              </p>
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-8">#</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Input</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Expected Output</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plainTestCases.map((tc, ti) => (
                      <tr key={ti} className={cn('border-t border-border/30', ti % 2 === 0 ? 'bg-background' : 'bg-muted/10')}>
                        <td className="px-3 py-2 text-muted-foreground font-mono">{ti + 1}</td>
                        <td className="px-3 py-2 font-mono text-foreground whitespace-pre-wrap break-all">{tc.input != null ? (typeof tc.input === 'object' ? JSON.stringify(tc.input) : String(tc.input)) : '—'}</td>
                        <td className="px-3 py-2 font-mono text-foreground whitespace-pre-wrap break-all">{tc.expected != null ? (typeof tc.expected === 'object' ? JSON.stringify(tc.expected) : String(tc.expected)) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-amber-500" />No test case data available for this question
            </p>
          )}

          {/* Code Comprehension — voice follow-up result for this question
              (also shown on the Cheating-Evidence page). Renders only when a
              follow-up session was recorded. */}
          {question.followup_session && (
            <FollowupComprehension fs={question.followup_session} />
          )}
        </div>
      )}
    </div>
  );
}

function CodingDetailModal({ open, onClose, d, codingTestId, candidateName, jobTitle }) {
  const printRef    = useRef();
  const hiddenPrintRef = useRef();
  const [questions, setQuestions]   = useState([]);
  const [qLoading, setQLoading]     = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!codingTestId) { setQuestions([]); return; }
    setQLoading(true);

    // Fetch both endpoints in parallel:
    // 1) submission_results → has candidate code + actual test outputs + pass/fail (new tests)
    // 2) coding_questions_for_report → has descriptions + plain test cases (fallback)
    Promise.allSettled([
      fetch(`${API_BASE_URL}/api/coding_submission_results?test_id=${encodeURIComponent(codingTestId)}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API_BASE_URL}/api/coding_questions_for_report?test_id=${encodeURIComponent(codingTestId)}`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([subRes, qRes]) => {
      const subQs = subRes.status === 'fulfilled' && Array.isArray(subRes.value?.questions) ? subRes.value.questions : [];
      const plainQs = qRes.status === 'fulfilled' && Array.isArray(qRes.value?.questions) ? qRes.value.questions : [];

      if (subQs.length > 0) {
        // Submission results are self-contained (include description, code, test_results)
        // Enrich with test_cases from questions file if needed (for plain fallback display)
        const enriched = subQs.map((sq, i) => ({
          ...sq,
          test_cases: plainQs[i]?.test_cases || [],
        }));
        setQuestions(enriched);
      } else {
        // Fallback: show plain question data (no submission data)
        setQuestions(plainQs);
      }
    }).catch(() => setQuestions([]))
      .finally(() => setQLoading(false));
  }, [open, codingTestId]);

  const handleDownload = () => {
    if (!hiddenPrintRef.current) return;
    const pw = window.open('', '_blank');
    if (!pw) {
      toast.error('Pop-up blocked', { description: 'Allow pop-ups to download the coding report.' });
      return;
    }
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(l => `<link rel="stylesheet" href="${l.href}">`)
      .join('');
    const styles = Array.from(document.querySelectorAll('style'))
      .map(s => `<style>${s.innerHTML}</style>`)
      .join('');
    pw.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8">
      <title>Coding Report — ${candidateName}</title>
      ${links}${styles}
      <style>
        /* Remove browser date/time/URL/page-number from printed headers & footers */
        @page {
          margin: 0;
          size: auto;
        }

        /* Compensate with body padding so content has breathing room */
        body {
          margin: 0;
          padding: 18mm 20mm;
          box-sizing: border-box;
        }

        /* Print overrides */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Full code — no scroll, no height cap */
        pre {
          max-height: none !important;
          height: auto !important;
          overflow: visible !important;
          white-space: pre-wrap !important;
          word-break: break-word !important;
        }

        /* Prevent blank trailing page */
        html, body {
          height: auto !important;
          overflow: visible !important;
        }
        body > div:last-child {
          page-break-after: avoid !important;
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
      </style>
    </head><body>${hiddenPrintRef.current.innerHTML}</body></html>`);
    pw.document.close();
    setTimeout(() => { pw.print(); pw.close(); }, 800);
  };

  const scoreMetrics = [
    { label: 'Correctness',     value: d.correctness      },
    { label: 'Efficiency',      value: d.efficiency        },
    { label: 'Performance',     value: d.performance       },
    { label: 'Robustness',      value: d.robustness        },
    { label: 'Readability',     value: d.readability_score },
    { label: 'Code Modularity', value: d.code_modularity   },
  ];

  const hasPlag = d.plagiarism_percentage != null;
  const plagPct = parseInt(d.plagiarism_percentage ?? 0);
  const highPlag = plagPct > 40;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">

        {/* ── Sticky header ── */}
        <div className="sticky top-0 z-10 bg-background border-b border-border/60 px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
              <Code2 className="w-4.5 h-4.5 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold leading-none truncate">Coding Test — Detailed Report</DialogTitle>
              <DialogDescription className="text-[12px] text-muted-foreground mt-0.5 truncate">
                {candidateName}{jobTitle ? ` · ${jobTitle}` : ''}
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={handleDownload} className="gap-1.5 text-xs">
              <Download className="w-3.5 h-3.5" />
              Download
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onClose}
              className="h-8 w-8 p-0"
              aria-label="Close report"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* ── Printable body ── */}
        <div ref={printRef} className="px-6 py-5 space-y-6">

          {/* Candidate info header (for print) */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-foreground">{candidateName}</p>
              {jobTitle && <p className="text-sm text-muted-foreground">{jobTitle}</p>}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: '#4caf50' }}>{Math.round(d.coding_score ?? 0)}%</p>
              <p className="text-xs text-muted-foreground">Overall Coding Score</p>
            </div>
          </div>

          {/* ── Score metrics ── */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />Code Quality Metrics
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scoreMetrics.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border/40">
                  <span className="text-sm font-medium">{label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={cn('h-full rounded-full', getScoreColor(value ?? 0))} style={{ width: `${Math.min(100, value ?? 0)}%` }} />
                    </div>
                    <span className="text-sm font-semibold tabular-nums w-10 text-right">{Math.round(value ?? 0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Plagiarism ── */}
          {hasPlag && (
            <div className={cn('rounded-lg border px-4 py-3', highPlag ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200')}>
              <div className="flex items-center gap-2 mb-1">
                {highPlag
                  ? <AlertTriangle className="w-4 h-4 text-red-600" />
                  : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                <span className={cn('text-sm font-semibold', highPlag ? 'text-red-700' : 'text-green-700')}>
                  Plagiarism — {plagPct}% similarity
                </span>
              </div>
              <p className={cn('text-xs', highPlag ? 'text-red-600' : 'text-green-700')}>
                {highPlag ? '⚠ Significant similarity detected — manual review recommended' : '✓ Minimal similarity — code appears original'}
              </p>
            </div>
          )}

          {/* ── Analysis cards ── */}
          {[
            { icon: <FileCode className="w-3.5 h-3.5" />, title: 'Code Summary',            value: d.coding_summary },
            { icon: <Building2 className="w-3.5 h-3.5" />, title: 'Code Architecture',      value: d.code_architecture },
            { icon: <Zap className="w-3.5 h-3.5" />,       title: 'Performance Optimization',value: d.performance_optimization },
            { icon: <Brain className="w-3.5 h-3.5" />,     title: 'Algorithm Selection',     value: d.algorithm_selection },
          ].filter(c => c.value).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />Code Analysis
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: <FileCode className="w-3.5 h-3.5" />, title: 'Code Summary',             value: d.coding_summary },
                  { icon: <Building2 className="w-3.5 h-3.5" />, title: 'Code Architecture',       value: d.code_architecture },
                  { icon: <Zap className="w-3.5 h-3.5" />,       title: 'Performance Optimization', value: d.performance_optimization },
                  { icon: <Brain className="w-3.5 h-3.5" />,     title: 'Algorithm Selection',      value: d.algorithm_selection },
                ].filter(c => c.value).map(({ icon, title, value }) => (
                  <div key={title} className="rounded-lg border border-border/50 p-3">
                    <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5 mb-1.5">{icon}{title}</p>
                    <ul className="space-y-1">
                      {value.split(',').map((pt, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-foreground">
                          <span className="text-violet-500 mt-1 flex-shrink-0">•</span>
                          <span>{pt.trim()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Strengths / Weaknesses ── */}
          {(d.coding_strengths || d.coding_weaknesses) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {d.coding_strengths && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-3">
                  <p className="text-[11px] font-semibold text-green-700 flex items-center gap-1.5 mb-2">
                    <CheckCircle2 className="w-3.5 h-3.5" />Strengths
                  </p>
                  <ul className="space-y-1">
                    {d.coding_strengths.replace(/[\[\]']+/g, "").split(",").map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-green-800">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {d.coding_weaknesses && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                  <p className="text-[11px] font-semibold text-red-700 flex items-center gap-1.5 mb-2">
                    <XCircle className="w-3.5 h-3.5" />Areas to Improve
                  </p>
                  <ul className="space-y-1">
                    {d.coding_weaknesses.replace(/[\[\]']+/g, "").split(",").map((s, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs text-red-800">
                        <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Questions & Test Cases ── */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5" />Questions &amp; Test Cases
            </p>
            {qLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="w-4 h-4 animate-spin" />Loading questions…
              </div>
            ) : questions.length > 0 ? (
              <div className="space-y-2">
                {questions.map((q, i) => (
                  <QuestionAccordion key={i} question={q} index={i} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 px-4 py-6 text-center">
                <FlaskConical className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground font-medium">Question data not available</p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">
                  Question files are only kept temporarily after a test is generated.
                </p>
              </div>
            )}
          </div>

        </div>

        {/* ── Hidden print container — all questions always expanded, never visible ── */}
        <div ref={hiddenPrintRef} style={{ display: 'none' }} aria-hidden="true">
          <div className="px-6 py-5 space-y-6">

            {/* Candidate header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-foreground">{candidateName}</p>
                {jobTitle && <p className="text-sm text-muted-foreground">{jobTitle}</p>}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: '#4caf50' }}>{Math.round(d.coding_score ?? 0)}%</p>
                <p className="text-xs text-muted-foreground">Overall Coding Score</p>
              </div>
            </div>

            {/* Score metrics */}
            <div>
              <p style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                Code Quality Metrics
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {scoreMetrics.map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>{label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '80px', height: '6px', background: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '9999px', width: `${Math.min(100, value ?? 0)}%`, backgroundColor: getScoreColorHex(value ?? 0) }} />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '40px', textAlign: 'right' }}>{Math.round(value ?? 0)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Plagiarism */}
            {hasPlag && (
              <div className={cn('rounded-lg border px-4 py-3', highPlag ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200')}>
                <div className="flex items-center gap-2 mb-1">
                  {highPlag
                    ? <AlertTriangle className="w-4 h-4 text-red-600" />
                    : <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  <span className={cn('text-sm font-semibold', highPlag ? 'text-red-700' : 'text-green-700')}>
                    Plagiarism — {plagPct}% similarity
                  </span>
                </div>
                <p className={cn('text-xs', highPlag ? 'text-red-600' : 'text-green-700')}>
                  {highPlag ? '⚠ Significant similarity detected — manual review recommended' : '✓ Minimal similarity — code appears original'}
                </p>
              </div>
            )}

            {/* Analysis cards */}
            {[
              { icon: <FileCode className="w-3.5 h-3.5" />, title: 'Code Summary',             value: d.coding_summary },
              { icon: <Building2 className="w-3.5 h-3.5" />, title: 'Code Architecture',       value: d.code_architecture },
              { icon: <Zap className="w-3.5 h-3.5" />,       title: 'Performance Optimization', value: d.performance_optimization },
              { icon: <Brain className="w-3.5 h-3.5" />,     title: 'Algorithm Selection',      value: d.algorithm_selection },
            ].filter(c => c.value).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />Code Analysis
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: <FileCode className="w-3.5 h-3.5" />, title: 'Code Summary',             value: d.coding_summary },
                    { icon: <Building2 className="w-3.5 h-3.5" />, title: 'Code Architecture',       value: d.code_architecture },
                    { icon: <Zap className="w-3.5 h-3.5" />,       title: 'Performance Optimization', value: d.performance_optimization },
                    { icon: <Brain className="w-3.5 h-3.5" />,     title: 'Algorithm Selection',      value: d.algorithm_selection },
                  ].filter(c => c.value).map(({ icon, title, value }) => (
                    <div key={title} className="rounded-lg border border-border/50 p-3">
                      <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5 mb-1.5">{icon}{title}</p>
                      <ul className="space-y-1">
                        {value.split(',').map((pt, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-foreground">
                            <span className="text-violet-500 mt-1 flex-shrink-0">•</span>
                            <span>{pt.trim()}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths / Weaknesses */}
            {(d.coding_strengths || d.coding_weaknesses) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {d.coding_strengths && (
                  <div className="rounded-lg border border-green-200 bg-green-50/50 p-3">
                    <p className="text-[11px] font-semibold text-green-700 flex items-center gap-1.5 mb-2">
                      <CheckCircle2 className="w-3.5 h-3.5" />Strengths
                    </p>
                    <ul className="space-y-1">
                      {d.coding_strengths.replace(/[\[\]']+/g, "").split(",").map((s, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-green-800">
                          <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.coding_weaknesses && (
                  <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                    <p className="text-[11px] font-semibold text-red-700 flex items-center gap-1.5 mb-2">
                      <XCircle className="w-3.5 h-3.5" />Areas to Improve
                    </p>
                    <ul className="space-y-1">
                      {d.coding_weaknesses.replace(/[\[\]']+/g, "").split(",").map((s, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-red-800">
                          <span className="mt-1 flex-shrink-0">•</span><span>{s.trim()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Questions — always expanded via forceOpen */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5" />Questions &amp; Test Cases
              </p>
              <div className="space-y-2">
                {questions.map((q, i) => (
                  <QuestionAccordion key={i} question={q} index={i} forceOpen={true} />
                ))}
              </div>
            </div>

          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable sub-components
// ─────────────────────────────────────────────────────────────────────────────

const SectionDivider = ({ icon, label }) => (
  <div className="flex items-center gap-3">
    <Separator className="flex-1" />
    <Badge variant="secondary" className="text-xs font-medium px-2.5 py-0.5">
      {icon}{label}
    </Badge>
    <Separator className="flex-1" />
  </div>
);

const TraitTable = ({ icon, heading, rows }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      {icon}
      <Heading level="h4" className="text-base font-semibold">{heading}</Heading>
    </div>
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">Section</TableHead>
            <TableHead className="font-semibold">Description</TableHead>
            <TableHead className="font-semibold text-right">Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ label, description, score, outOf = 10 }, i) => (
            <TableRow key={i} className="hover:bg-muted/30">
              <TableCell className="font-medium">{label}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{description}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn("h-full rounded-full", getScoreColor((score / outOf) * 100))}
                      style={{ width: `${(score / outOf) * 100}%` }}
                    />
                  </div>
                  <Text size="sm" className="font-semibold min-w-[2rem]">{score}/{outOf}</Text>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const PlagiarismCard = ({ plagiarism, codingTestId, onOpenEvidence = null }) => {
  const pct  = parseInt(plagiarism ?? 15);
  const high = pct > 40;
  // The single-number `plagiarism_percentage` is just a roll-up. The Phase 8
  // Evidence Dashboard reads cheating_events directly and shows each signal
  // (MOSS / AI / online / style) in its own panel. Show a "View evidence"
  // link whenever we have a coding test id so recruiters can dig deeper.
  //
  // When the parent supplied `onOpenEvidence`, render the link as a button
  // that calls it — the parent then renders <CheatingEvidence embedded /> in
  // the same page shell instead of triggering a full-page navigation. Falls
  // back to a plain anchor when no callback is wired, so other call sites
  // (embedded Reports in All.jsx without inline-evidence yet) still work.
  return (
    <Card className={cn('border', high ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50')}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2">
            {high
              ? <AlertTriangle className="w-4 h-4 text-red-600" />
              : <CheckCircle2  className="w-4 h-4 text-green-600" />}
            Plagiarism Check
          </span>
          {codingTestId && (
            typeof onOpenEvidence === 'function' ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenEvidence(codingTestId);
                }}
                className="text-xs font-normal text-blue-600 hover:underline"
                title="Open the full per-signal evidence dashboard (inline)"
              >
                View evidence →
              </button>
            ) : (
              <a
                href={`/hirewithus/cheating-evidence/${encodeURIComponent(codingTestId)}`}
                className="text-xs font-normal text-blue-600 hover:underline"
                title="Open the full per-signal evidence dashboard"
              >
                View evidence →
              </a>
            )
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {pct === 0 ? (
          <Text size="sm" variant="muted">No code submitted for analysis</Text>
        ) : (
          <>
            <div className="flex items-center justify-between py-1.5 px-2 rounded-md">
              <Text size="sm" className="font-medium">Similarity</Text>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full', high ? 'bg-red-500' : 'bg-green-500')}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>
                <Text size="sm" className={cn('font-semibold min-w-[3rem] text-right', high ? 'text-red-600' : 'text-green-700')}>
                  {pct}%
                </Text>
              </div>
            </div>
            <div className="px-2">
              <Text size="xs" className={high ? 'text-red-600 font-medium' : 'text-green-700'}>
                {high ? '⚠ Significant similarity — manual review required' : '✓ Minimal similarity — code appears original'}
              </Text>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const BulletCard = ({ icon, title, value }) => (
  <Card className="border">
    <CardHeader className="pb-3">
      <CardTitle className="text-base font-semibold flex items-center gap-2">{icon}{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {value ? (
        <ul className="list-none space-y-1.5">
          {value.split(',').map((pt, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span className="text-primary mt-1.5 flex-shrink-0">•</span>
              <span>{pt.trim()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <Text size="sm" variant="muted">No analysis available</Text>
      )}
    </CardContent>
  </Card>
);

export default Reports;